import { useEffect, useRef, useState } from 'react';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void> | void;
  threshold?: number;
  enabled?: boolean;
}

export const usePullToRefresh = ({
  onRefresh,
  threshold = 80,
  enabled = true
}: UsePullToRefreshOptions) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startYRef = useRef<number>(0);
  const currentYRef = useRef<number>(0);
  const elementRef = useRef<HTMLElement | null>(null);
  const onRefreshRef = useRef(onRefresh);
  const isPullingRef = useRef(false);
  const pullDistanceRef = useRef(0);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled) return;

    const element = elementRef.current || window;
    let startScrollTop = 0;

    const handleTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!touchEvent.touches || touchEvent.touches.length === 0) return;

      const touch = touchEvent.touches[0];
      startYRef.current = touch.clientY;
      currentYRef.current = touch.clientY;

      // Verificar se está no topo da página
      if (element === window) {
        startScrollTop = window.scrollY || window.pageYOffset;
      } else {
        startScrollTop = (element as HTMLElement).scrollTop;
      }
    };

    const handleTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!touchEvent.touches || touchEvent.touches.length === 0) return;

      const touch = touchEvent.touches[0];
      currentYRef.current = touch.clientY;
      const deltaY = currentYRef.current - startYRef.current;

      // Só permitir pull se estiver no topo
      if (startScrollTop <= 0 && deltaY > 0) {
        isPullingRef.current = true;
        e.preventDefault();

        const distance = Math.min(deltaY, threshold * 1.5);
        setPullDistance(distance);
        pullDistanceRef.current = distance;
      }
    };

    const handleTouchEnd = async () => {
      if (isPullingRef.current && pullDistanceRef.current >= threshold) {
        setIsRefreshing(true);
        setPullDistance(0);
        pullDistanceRef.current = 0;

        try {
          await onRefreshRef.current();
        } finally {
          setIsRefreshing(false);
        }
      } else {
        setPullDistance(0);
        pullDistanceRef.current = 0;
      }

      isPullingRef.current = false;
    };

    element.addEventListener('touchstart', handleTouchStart as EventListener, { passive: false });
    element.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });
    element.addEventListener('touchend', handleTouchEnd as EventListener);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart as EventListener);
      element.removeEventListener('touchmove', handleTouchMove as EventListener);
      element.removeEventListener('touchend', handleTouchEnd as EventListener);
    };
  }, [enabled, threshold]);

  return {
    isRefreshing,
    pullDistance,
    pullProgress: Math.min(pullDistance / threshold, 1),
    elementRef
  };
};
